import React, {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {useParams} from "react-router-dom";
import clinicService from "../../../../../services/clinic.service";
import Loading from "../../../../common/Loading";
import Swal from 'sweetalert2';

const SocialLinksTable = () => {
    const {clinicId} = useParams();
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);


    useEffect(() => {
        fetchLinks();
    }, [clinicId]);

    const fetchLinks = () => {
        setLoading(true);
        clinicService.getSocialLinks(clinicId)
            .then(res => {
                setLinks(res.data);
                setLoading(false);
            })
            .catch(err => {
                setLoading(false);
            });
    };

    // Enhanced delete handler with SweetAlert
    const handleDelete = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `
            <i class="bi bi-check"></i>
            Yes, delete it!`,
            cancelButtonText: `
            <i class="bi bi-x"></i> 
            Cancel`,
            reverseButtons: true,
            focusCancel: true,
            customClass: {
                confirmButton: `btn btn-success btn-sm me-2`,
                cancelButton: 'btn btn-secondary btn-sm',
                title: `card-title`,
                html: `small`
            }
        }).then((result) => {
            if (result.isConfirmed) {
                clinicService.deleteSocialLink(clinicId, id)
                    .then(() => {
                        // Optimistically update the UI
                        const updatedLinks = links.filter(link => link.id !== id);
                        setLinks(updatedLinks);

                        Swal.fire(
                            'Deleted!',
                            'Your social link has been deleted.',
                            'success'
                        );

                        // If we deleted the last item, the empty state will automatically show
                    })
                    .catch(err => {
                        Swal.fire(
                            'Error!',
                            'Failed to delete the link.',
                            'error'
                        );
                        // Refresh to get the correct state if deletion failed
                        fetchLinks();
                    });
            }
        });
    };

    return (
        <div className="container-fluid p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="card-title mb-0">
                    <i className="bi bi-share me-2"></i>
                    Social Links Management
                </div>
                <button
                    className={`btn ${showAddForm ? 'btn-outline-danger' : 'btn-outline-primary'} btn-sm`}
                    onClick={() => setShowAddForm(!showAddForm)}
                >
                    <i className={`bi ${showAddForm ? 'bi-x-lg' : 'bi-plus-lg'} me-2`}></i>
                    {showAddForm ? 'Cancel' : 'Add Link'}
                </button>
            </div>

            {/* Add New Form */}
            {showAddForm && (
                <AddLinkForm
                    clinicId={clinicId}
                    onSuccess={() => {
                        setShowAddForm(false);
                        fetchLinks();
                    }}
                />
            )}

            {/* Main Table */}
            <div className="card shadow-none card-padding">
                <div className="card-body p-0">
                    {loading ? (
                        <Loading/>
                    ) : links.length === 0 ? (
                        <div className="alert alert-danger">
                            <div className=" text-center py-5">
                                <i className="bi bi-share fs-1 text-muted mb-3"></i>
                                <h5>No Social Links Found</h5>
                                <p className="text-muted mb-4">
                                    Add your first social link to get started
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-bordered table-striped table-hover mb-0">
                                <thead className="table-light">
                                <tr>
                                    <th><i className="bi bi-share"></i> Platform</th>
                                    <th><i className="bi bi-link"></i> Link</th>
                                    <th><i className="bi bi-clock-history"></i> Added On</th>
                                    <th><i className="bi bi-gear"></i> Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {links.map(link => (
                                    <TableRow
                                        key={link.id}
                                        link={link}
                                        isEditing={editingId === link.id}
                                        onEditStart={() => setEditingId(link.id)}
                                        onEditCancel={() => setEditingId(null)}
                                        onUpdateSuccess={() => {
                                            setEditingId(null);
                                            fetchLinks();
                                        }}
                                        onDelete={handleDelete}  // Pass the enhanced delete handler
                                    />
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Table Row Component with Inline Edit
const TableRow = ({link, isEditing, onEditStart, onEditCancel, onUpdateSuccess, onDelete}) => {
    const {register, handleSubmit, formState: {errors, isSubmitting}} = useForm({
        defaultValues: {
            id: link.id,
            type: link.type,
            clinicId: link.clinicId,
            link: link.link
        }
    });

    const onSubmit = (data) => {
        clinicService.updateSocialLinks(link.clinicId, data)
            .then(() => {
                onUpdateSuccess();
            })
            .catch(err => toast.error("Update failed"));
    };

    const getPlatformIcon = (type) => {
        const icons = {
            WEBSITE: "bi-globe",
            GOOGLE_PAGE: "bi-google",
            FACEBOOK: "bi-facebook",
            INSTAGRAM: "bi-instagram",
            TWITTER: "bi-twitter",
            YELP: "bi-star-fill",
            OTHER: "bi-link-45deg"
        };
        return icons[type] || "bi-link-45deg";
    };

    const formatPlatformName = (type) => {
        return type.replace('_', ' ');
    };

    return (
        <tr>

            {!isEditing ? (
                <>
                    <td className="align-middle">
                        <i className={`bi ${getPlatformIcon(link.type)} me-2`}></i>
                        {formatPlatformName(link.type)}
                    </td>
                    <td className="align-middle">
                        <a href={link.link} target="_blank" rel="noopener noreferrer">
                            {link.link}
                        </a>
                    </td>
                    <td className="align-middle text-muted small">
                        {link.createdAt}
                    </td>
                    <td className="align-middle">
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={onEditStart}
                            >
                                <i className="bi bi-pencil"></i>
                            </button>
                            <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => onDelete(link.id)}  // This will trigger SweetAlert
                            >
                                <i className="bi bi-trash"></i>
                            </button>
                        </div>
                    </td>
                </>
            ) : (
                <>
                    <td colSpan="4">
                        <form onSubmit={handleSubmit(onSubmit)} className="row g-2 align-items-center">
                            <div className="col-md-3">
                                <select
                                    className={`form-select form-select-sm ${errors.type ? 'is-invalid' : ''}`}
                                    {...register("type", {required: true})}
                                >
                                    <option value="WEBSITE">Website</option>
                                    <option value="GOOGLE_PAGE">Google</option>
                                    <option value="FACEBOOK">Facebook</option>
                                    <option value="INSTAGRAM">Instagram</option>
                                    <option value="TWITTER">Twitter</option>
                                    <option value="YELP">Yelp</option>
                                    <option value="OTHER">Other</option>
                                </select>
                            </div>
                            <div className="col-md-5">
                                <input
                                    type="url"
                                    className={`form-control form-control-sm ${errors.link ? 'is-invalid' : ''}`}
                                    {...register("link", {
                                        required: true,
                                        pattern: /^https?:\/\/.+/
                                    })}
                                />
                            </div>
                            <div className="col-md-4">
                                <div className="d-flex gap-2">
                                    <button
                                        type="submit"
                                        className="btn btn-sm btn-primary"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <span className="spinner-border spinner-border-sm"></span>
                                        ) : 'Save'}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={onEditCancel}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                            <input type="hidden" {...register("id")} />
                            <input type="hidden" {...register("clinicId")} />
                        </form>
                    </td>
                </>
            )}
        </tr>
    );
};

// Add New Form Component
const AddLinkForm = ({clinicId, onSuccess}) => {
    const {register, handleSubmit, formState: {errors, isSubmitting}, reset} = useForm();

    const onSubmit = (data) => {
        clinicService.updateSocialLinks(clinicId, data)
            .then(() => {
                reset();
                onSuccess();
            })
            .catch(err => toast.error("Add failed"));
    };

    return (
        <div className="card mb-3 card-padding shadow-none card-border">
            <div className="card-body">
                <form onSubmit={handleSubmit(onSubmit)} className="row g-3 align-items-end">
                    <div className="col-md-3">
                        <label className="form-label">Platform</label>
                        <select
                            className={`form-select ${errors.type ? 'is-invalid' : ''}`}
                            {...register("type", {required: true})}
                        >
                            <option value="">Select platform</option>
                            <option value="WEBSITE">Website</option>
                            <option value="GOOGLE_PAGE">Google Business</option>
                            <option value="FACEBOOK">Facebook</option>
                            <option value="INSTAGRAM">Instagram</option>
                            <option value="TWITTER">Twitter</option>
                            <option value="YELP">Yelp</option>
                            <option value="OTHER">Other</option>
                        </select>
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">URL</label>
                        <input
                            type="url"
                            className={`form-control ${errors.link ? 'is-invalid' : ''}`}
                            placeholder="https://example.com"
                            {...register("link", {
                                required: true,
                                pattern: /^https?:\/\/.+/
                            })}
                        />
                        <input type="hidden" {...register("clinicId")} value={clinicId}/>
                    </div>
                    <div className="col-md-3">
                        <button
                            type="submit"
                            className="btn btn-success w-100"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <span className="spinner-border spinner-border-sm me-2"></span>
                            ) : (
                                <i className="bi bi-floppy me-2"></i>
                            )}
                            Save
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SocialLinksTable;